import { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { useMemo } from 'react'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel } from '../ImporterViewModel'
import { columnOptions } from '../ImporterViewModel'

export function ImportWizardFinishMatchingStep({
  show,
  data,
  viewModel,
  selectedAction,
  onActionChange,
}: {
  show: boolean
  data: CSVRow[]
  viewModel: ReturnType<typeof useImporterViewModel>
  selectedAction: 'create' | 'exclude'
  onActionChange: (action: 'create' | 'exclude') => void
}) {
  // Identify company_match columns from headers
  const companyMatchHeaders = useMemo(() => {
    return (
      viewModel.importData?.headers.filter(
        (h) => columnOptions[h.mapped]?.type === 'company_match'
      ) ?? []
    )
  }, [viewModel.importData?.headers])

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    const columns: ColumnDef<CSVRow>[] = []

    // Input Data column
    columns.push({
      id: 'input_data',
      header: 'Input Data',
      cell: ({ row }) => {
        const items: { label: string; value: string }[] = []

        companyMatchHeaders.forEach((header) => {
          const value = row.original[header.original]
          if (value) {
            items.push({
              label: columnOptions[header.mapped]?.name || header.mapped,
              value,
            })
          }
        })

        return (
          <div className="space-y-0.5 text-sm">
            {items.map((item, idx) => (
              <div key={idx} className="text-gray-700">
                <span className="font-medium text-gray-500">{item.label}:</span>{' '}
                {item.value}
              </div>
            ))}
          </div>
        )
      },
    })

    // Matched Data column (sortable)
    columns.push({
      id: 'matched_data',
      header: 'Matched Data',
      cell: ({ row }) => {
        const chain = row.original[InternalColumns.MATCHED_CHAIN]
        const door = row.original[InternalColumns.MATCHED_DOOR]
        const isMatched = chain || door

        if (!isMatched) {
          return (
            <div className="text-gray-400 italic flex items-center gap-2">
              <span className="text-gray-400">○</span>
              <span>Unmatched</span>
            </div>
          )
        }

        const items: { label: string; value: string }[] = []

        if (door?.name || chain?.name) {
          items.push({ label: 'Name', value: door?.name || chain?.name || '-' })
        }
        if (door?.domain || chain?.domain) {
          items.push({
            label: 'Domain',
            value: door?.domain || chain?.domain || '-',
          })
        }
        if (door?.address) {
          items.push({ label: 'Address', value: door.address })
        }
        if (chain?.count || door?.chain_size) {
          items.push({
            label: 'Doors',
            value: String(chain?.count || door?.chain_size || '-'),
          })
        }

        return (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-green-600 font-bold">✓</span>
              <span className="font-semibold text-gray-900 text-sm">
                Matched
              </span>
            </div>
            <div className="space-y-0.5 text-sm">
              {items.map((item, idx) => (
                <div key={idx} className="text-gray-700">
                  <span className="font-medium text-gray-500">
                    {item.label}:
                  </span>{' '}
                  {item.value}
                </div>
              ))}
            </div>
          </div>
        )
      },
      enableSorting: true,
      sortingFn: 'alphanumeric',
    })

    return columns
  }, [companyMatchHeaders])

  if (!show) {
    return null
  }

  return (
    <>
      <div className="text-sm text-gray-600">
        <span className="font-semibold">{data.length}</span> total row
        {data.length !== 1 ? 's' : ''}
        {viewModel.amountCompaniesPendingReview > 0 && (
          <> ({viewModel.amountCompaniesLeftUnmatched} unmatched)</>
        )}
      </div>

      <ClientDataTable
        height={viewModel.amountCompaniesLeftUnmatched > 0 ? 400 : '100%'}
        data={data}
        columns={columnDefs}
        enableSorting
      />

      {viewModel.amountCompaniesLeftUnmatched > 0 && (
        <div className="flex flex-col gap-3 mt-4">
          <p className="text-sm font-semibold text-gray-700">
            What would you like to do with unmatched rows?
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => onActionChange('create')}
              className={`border-2 rounded-lg p-4 text-left transition-all ${
                selectedAction === 'create'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  checked={selectedAction === 'create'}
                  onChange={() => {}}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">
                    Create Custom Companies
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    These rows will be imported as custom companies with
                    Other/Other category
                  </div>
                </div>
                {selectedAction === 'create' && (
                  <div className="text-green-600 font-bold text-lg">✓</div>
                )}
              </div>
            </button>

            <button
              type="button"
              onClick={() => onActionChange('exclude')}
              className={`border-2 rounded-lg p-4 text-left transition-all ${
                selectedAction === 'exclude'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  checked={selectedAction === 'exclude'}
                  onChange={() => {}}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">
                    Exclude from Import
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    These rows will not be imported
                  </div>
                </div>
                {selectedAction === 'exclude' && (
                  <div className="text-green-600 font-bold text-lg">✓</div>
                )}
              </div>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
